import * as React from 'react'
import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

export async function getPublicUser(userId: number, { withToken = false }: { withToken?: boolean } = {}) {
  const result = await api.client('GET /users/:id', {
    urlParams: { id: String(userId) },
    withToken: withToken,
  })

  return result.next((response) => response.body)
}

export async function deletePublicUser(userId: number, { dryRun = false }: { dryRun?: boolean } = {}) {
  const result = await api.client('DELETE /users/:id', {
    body: dryRun ? { dry_run: true } : {},
    getParams: {
      timestamp: new Date().getTime().toString(),
    },
    urlParams: { id: String(userId) },
    withToken: true,
  })

  return result.next((response) => response.body)
}

export const PUBLIC_USERS_CACHE = jotai.atom<hooks.Cache<api.PublicUser>>({})
export const DELETE_USER_DRY_RUN_CACHE = jotai.atom<Record<string, api.DeleteUserResponse>>({})

export function setInitialPublicUser(store: ReturnType<typeof jotai.createStore>, publicUser: api.PublicUser): void {
  store.set(PUBLIC_USERS_CACHE, (cache) => {
    cache[id(publicUser.id)] = {
      data: publicUser,
      status: hooks.QueryStatus.SUCCESS,
    }
    return cache
  })
}

export type PublicUserConfig = {
  fetchOnMount?: boolean | 'force'
  withToken?: boolean
}

export type UsePublicUser = {
  response: hooks.Response<api.PublicUser>
  query: () => Promise<void>
  deleteUser: (dryRun?: boolean) => Promise<api.HttpResult<unknown> | undefined>
  dryRunDeleteUser: api.DeleteUserResponse | undefined
}

export function usePublicUser(
  userId?: number,
  { fetchOnMount = true, withToken = false }: PublicUserConfig = {},
): UsePublicUser {
  const sharedQuery = hooks.useSharedQuery(
    (userId: number, withToken: boolean) => {
      return getPublicUser(userId, { withToken: withToken })
    },
    {
      atom: PUBLIC_USERS_CACHE,
      id,
    },
  )

  console.log({sharedQuery})

  const [dryRunDeleteUser, setDryRunDeleteUser] = jotai.useAtom(DELETE_USER_DRY_RUN_CACHE)

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(userId))

  return { response: getResponse(), query, deleteUser, dryRunDeleteUser: dryRunDeleteUser[String(userId)] }

  function getResponse(): hooks.Response<api.PublicUser> {
    if (userId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    return sharedQuery.getCurrentResponse([userId, withToken])
  }

  async function query(): Promise<void> {
    if (userId !== undefined) {
      return sharedQuery.query(userId, withToken)
    }
  }

  async function deleteUser(dryRun?: boolean): Promise<api.HttpResult<unknown> | undefined> {
    if (userId === undefined) {
      return
    }

    if (dryRun) {
      const deleteUserResponse = await deletePublicUser(userId, { dryRun })
      return deleteUserResponse.next((success) => {
        setDryRunDeleteUser({ [String(userId)]: success })
      })
    } else {
      return sharedQuery.remove([userId, withToken], async () => {
        const deleteUserResponse = await deletePublicUser(userId, { dryRun })

        return deleteUserResponse.next(() => {
          setDryRunDeleteUser((cache) => {
            delete cache[String(userId)]
            return cache
          })
        })
      })
    }
  }
}

function id(userId?: number): string {
  return JSON.stringify([userId])
}
